import { Stack } from 'common/ui/Layout';
import Text from 'common/ui/Text';
import { Colors } from 'styles';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Icon } from 'common/ui/Icon';
import { css } from 'styled-components';
import { StatusIndicator } from 'domains/reporter/Reporter/Toolbars/ReportToolbar/StatusIndicator';
import { ReportSignedByLabel } from './ReportSignedByLabel';
import { logger } from 'modules/logger';
import { FocusModeToggleMethod } from 'hooks/useFocusMode';
import { ReporterSaveStateType } from 'config/recoilState';

type UnifiedEditorTopBarProps = {
  isFocusModeSettingOn: boolean;
  toggleFocusMode: (args: { method: FocusModeToggleMethod; enabled?: boolean }) => void;
  isFocusModeOn: boolean;
  label?: string | null;
  isReportEditor: boolean;
  saveStatus: ReporterSaveStateType;
  lastUpdated?: Date | null;
  reportOwner?: { firstName: string; lastName: string } | null;
  reportSubmittedAt?: Date | null;
  isAddendumEditor: boolean;
  addendumOwner?: { firstName: string; lastName: string };
  addendumSubmittedAt?: Date | null;
  shouldShowFocusModeToggle: boolean;
  shouldShowStatusIndicator: boolean;
  shouldShowTemplateName: boolean;
  templateName: string | undefined;
};

export const UnifiedEditorTopBar = ({
  isFocusModeSettingOn,
  toggleFocusMode,
  isFocusModeOn,
  label,
  isReportEditor,
  saveStatus,
  lastUpdated,
  reportOwner,
  reportSubmittedAt,
  isAddendumEditor,
  addendumOwner,
  addendumSubmittedAt,
  shouldShowFocusModeToggle,
  shouldShowStatusIndicator,
  shouldShowTemplateName,
  templateName,
}: UnifiedEditorTopBarProps) => {
  return (
    <Stack
      alignX={isFocusModeSettingOn || shouldShowTemplateName || label != null ? 'between' : 'end'}
      alignY="center"
      css={css`
        margin-bottom: 2px !important;
      `}
    >
      {label != null && (
        <Text
          variant="body1"
          color={Colors.gray8}
          css={
            isAddendumEditor
              ? css`
                  font-weight: bold;
                `
              : undefined
          }
        >
          {label}
        </Text>
      )}
      <Stack>
        {isFocusModeSettingOn && shouldShowFocusModeToggle && (
          <FormControlLabel
            data-testid="focus-mode-form-input"
            control={
              <Switch
                color="primary"
                size="small"
                onChange={() => {
                  logger.info('Focus mode toggled via form control');
                  toggleFocusMode({ method: FocusModeToggleMethod.FormControl });
                }}
              />
            }
            label={
              <Stack css={{ marginLeft: 16 }} alignY="center" space="xxsmall">
                <Icon name="focusMode" css="width: 16px; height: 16px;" />
                <Text variant="body2">Focus Mode</Text>
              </Stack>
            }
            labelPlacement="start"
            checked={isFocusModeOn}
          />
        )}
        {shouldShowTemplateName && (
          <Text
            variant="caption"
            css={css`
              margin-left: 14px;
              margin-top: 5px;
              color: ${Colors.gray8};
            `}
          >
            Template: {templateName == null ? 'Sirona Default' : templateName}
          </Text>
        )}
      </Stack>

      <Stack
        alignX="center"
        alignY="center"
        css={css`
          margin-right: 2px;
          ${isReportEditor ? 'height: 16px;' : ''}
        `}
      >
        {shouldShowStatusIndicator && (
          <div data-testid="status-indicator-wrapper">
            <StatusIndicator status={saveStatus} lastUpdated={lastUpdated} />
          </div>
        )}
        {!shouldShowStatusIndicator && reportSubmittedAt != null && !isAddendumEditor && (
          <ReportSignedByLabel
            firstName={reportOwner?.firstName}
            lastName={reportOwner?.lastName}
            submittedAt={reportSubmittedAt}
          />
        )}
        {isAddendumEditor && addendumSubmittedAt != null && (
          <ReportSignedByLabel
            firstName={addendumOwner?.firstName}
            lastName={addendumOwner?.lastName}
            submittedAt={addendumSubmittedAt}
          />
        )}
      </Stack>
    </Stack>
  );
};
